<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { organizationsApi, type Organization } from '@/services/api/organizations';
import { useAuthStore } from '@/stores/auth';
import { useNotifier } from '@/composables/notifier';
import AccountSubNav from './AccountSubNav.vue';

const auth = useAuthStore();
const notify = useNotifier();
const organization = ref<Organization | null>(null);
const submitting = ref(false);

onMounted(async () => {
  // Legacy `account.html` only rendered the org-edit form behind
  // `ng-if="user.organization_admin"`. Don't fetch (or expose) the org for
  // users who aren't their organization's admin.
  if (!auth.isSuperAdmin) return;
  const orgId = auth.currentUser?.organization_id;
  if (orgId !== null && orgId !== undefined) {
    organization.value = await organizationsApi.get(orgId);
  }
});

async function submit(): Promise<void> {
  if (!organization.value || submitting.value) return;
  submitting.value = true;
  try {
    organization.value = await organizationsApi.update(
      organization.value as Organization & { id: number },
    );
    notify.info('Organization updated.');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div>
    <AccountSubNav />
    <p v-if="!auth.isSuperAdmin" class="account-form__loading">
      You don’t have permission to edit this organization.
    </p>
    <form
      v-else-if="organization"
      class="account-form"
      novalidate
      @submit.prevent="submit"
    >
      <h2 class="account-form__title">Organization</h2>

      <label class="account-form__field" for="org-name">
        <span>Organization Name</span>
        <input id="org-name" v-model="organization.name" name="name" type="text" />
      </label>

      <label class="account-form__field" for="org-bio">
        <span>Organization Bio</span>
        <textarea id="org-bio" v-model="(organization as any).bio" name="bio" rows="10" />
      </label>

      <button type="submit" :disabled="submitting" class="account-form__submit">
        {{ submitting ? 'Saving…' : 'Update Organization' }}
      </button>
    </form>
    <p v-else class="account-form__loading">Loading…</p>
  </div>
</template>

<style scoped lang="scss" src="./account-form.scss"></style>
